//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { FormField } from '@campaigns/components/shared/product-details-modal/form/FormField';
import { VariantImagePreview } from '@campaigns/components/shared/product-details-modal/variants/VariantImagePreview';
import { VariantImageUploadPopover } from '@campaigns/components/shared/product-details-modal/variants/image-preview-popover/VariantImageUploadPopover';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  getVariationsValidationRules,
  isProductImageUrlValid,
} from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { validateProductIdentifier } from '@campaigns/utils/sampling/productIdentifierUtils';
import ActionButton from '@components/atoms/action-button/ActionButton';
import { TertiaryButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { CaptionErrorText } from '@components/atoms/text/Text';
import Popover from '@components/molecules/popover/Popover';
import { usePopover } from '@components/molecules/popover/usePopover';
import { TableDataCell } from '@components/organisms/table/TableDataCell';
import { TableRow } from '@components/organisms/table/TableRow';
import { isDefined, isTruthy } from '@utils/filterPredicates';

interface VariantRowProps {
  index: number;
  isViewOnly: boolean;
  onRemove: () => void;
  totalVariations: number;
}

export const VariantRow = ({
  index,
  isViewOnly,
  onRemove,
  totalVariations,
}: VariantRowProps) => {
  const { samplingCampaignRequest } = useSamplingCampaign();
  const { control, formState, getValues, setValue } =
    useFormContext<EditableBVSamplingProduct>();
  const { handleVariantImageSelect, setIsImageForAllVariants } =
    useProductModal();
  const [connectPopover, { closePopover }] = usePopover();

  const fulfillmentType =
    samplingCampaignRequest?.fulfillmentOptions?.fulfillmentType;
  const whoShipsProduct =
    samplingCampaignRequest?.fulfillmentOptions?.whoShipsProduct;
  const isBVShipped =
    fulfillmentType === 'shipped' && whoShipsProduct === 'bazaarvoice';

  const field = `variations.${index}.imgUrl` as const;
  const rules = getVariationsValidationRules(index, totalVariations);

  const initialImgUrl = getValues(field);
  const [showPreview, setShowPreview] = useState(!!initialImgUrl);

  const errors = formState.errors;
  const imageError = errors.variations?.[index]?.imgUrl?.message;

  const hasImage =
    isTruthy(initialImgUrl) &&
    showPreview &&
    (initialImgUrl.startsWith('blob:') ||
      isProductImageUrlValid(initialImgUrl));

  // Watch the GTIN value for this specific variation
  const gtinValue = useWatch({
    control,
    name: `variations.${index}.logisticsRef`,
  });

  // Compute validation state based on the watched value
  const isGtinValid = gtinValue
    ? validateProductIdentifier(gtinValue).isValid
    : false;

  const handleImageSubmit = (file: File | undefined) => {
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      setValue(field, blobUrl);
      setShowPreview(true);
    }
    handleVariantImageSelect(index, file);
    setIsImageForAllVariants(false);
  };

  const handleImageRemove = () => {
    const currentImgUrl = getValues(field);
    const allValues = getValues();

    // Only revoke the blob URL if it's not being used by the main image or other variants
    if (currentImgUrl?.startsWith('blob:')) {
      const isUsedByMainImage = allValues.imgUrl === currentImgUrl;

      // Check if any other variant is using this blob URL
      const isUsedByOtherVariants = allValues.variations.some(
        (variation, idx) => idx !== index && variation.imgUrl === currentImgUrl,
      );

      // Only revoke if this is the only place using the blob URL
      if (!isUsedByMainImage && !isUsedByOtherVariants) {
        URL.revokeObjectURL(currentImgUrl);
      }
    }

    setValue(field, '', {
      shouldValidate: false,
    });

    setShowPreview(false);
    handleVariantImageSelect(index, undefined);
    setIsImageForAllVariants(false);
  };

  const handlePreview = () => {
    const currentUrl = getValues(field);
    if (!isDefined(currentUrl)) {
      return;
    }
    if (isProductImageUrlValid(currentUrl)) {
      setShowPreview(true);
      setIsImageForAllVariants(false);
    }
  };

  return (
    <TableRow>
      {/* Image Field */}
      <StyledTableDataCell width={170}>
        {hasImage ? (
          <VariantImagePreview
            alt='Product preview'
            isViewOnly={isViewOnly}
            onRemove={handleImageRemove}
            src={getValues(field)}
          />
        ) : (
          <Popover
            connectPopover={connectPopover}
            content={
              <VariantImageUploadPopover
                index={index}
                onClearImage={handleImageRemove}
                onClosePopover={closePopover}
                onFileSelect={handleImageSubmit}
                onPreview={handlePreview}
                rules={rules[`variations.${index}.imgUrl`]}
              />
            }
            shouldOpenOnHover={false}
            target={
              <TertiaryButton
                aria-invalid={!!imageError}
                isDisabled={false}
                trackingId={
                  SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_VARIATION_IMAGE_POPOVER
                }
              >
                Add image
              </TertiaryButton>
            }
          />
        )}
        {imageError && (
          <ErrorText>
            <StyledIcon name='error' />
            <CaptionErrorText>{imageError}</CaptionErrorText>
          </ErrorText>
        )}
      </StyledTableDataCell>

      {/* Name Field */}
      <StyledTableDataCell width={330}>
        <FormField
          aria-label='Variant Name'
          inputType='textarea'
          isViewOnly={isViewOnly}
          label=''
          name={`variations.${index}.variantName`}
          placeholder=''
          rules={rules[`variations.${index}.variantName`]}
        />
      </StyledTableDataCell>

      {/* Quantity Field */}
      <StyledTableDataCell width={100}>
        <FormField
          aria-label='Variant Quantity'
          inputType='text'
          isViewOnly={isViewOnly}
          label=''
          name={`variations.${index}.quantity`}
          placeholder='10'
          rules={rules[`variations.${index}.quantity`]}
        />
      </StyledTableDataCell>

      {/* FMV Field */}
      <StyledTableDataCell width={100}>
        <FormField
          aria-label='Variant FMV'
          inputType='text'
          isViewOnly={isViewOnly}
          label=''
          name={`variations.${index}.fairMarketValue`}
          placeholder='25.99'
          rules={rules[`variations.${index}.fairMarketValue`]}
        />
      </StyledTableDataCell>

      {/* GTIN Field */}
      <StyledTableDataCell width={200}>
        <FormField
          aria-label='Variant Barcode Number (GTIN)'
          helpText={
            !isViewOnly && !isBVShipped && !isGtinValid && isTruthy(gtinValue)
              ? 'We highly recommend providing a valid barcode number (GTIN)'
              : ''
          }
          inputType='text'
          isViewOnly={isViewOnly}
          label=''
          labelHelpText='Enter a GTIN, EAN, or UPC to help us accurately identify your product. You can find this number near the barcode on the product packaging.'
          name={`variations.${index}.logisticsRef`}
          placeholder='1309945678912'
          rules={{
            ...rules[`variations.${index}.logisticsRef`],
            validate: (value: string) => {
              // For BV shipped products, enforce strict validation
              // For non-BV shipped products, show warning but allow submission
              if (!isBVShipped) {
                return true;
              }

              // For BV shipped products, return the error message if validation failed
              return validateProductIdentifier(value).reason ?? true;
            },
          }}
        />
      </StyledTableDataCell>

      {/* Action Button */}
      <ActionTableDataCell>
        {!isViewOnly && (
          <ActionButton
            aria-label='Remove variation'
            iconName='delete'
            isDisabled={index === 0}
            onClick={onRemove}
            trackingId={
              SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD_REMOVE_VARIATION
            }
          />
        )}
      </ActionTableDataCell>
    </TableRow>
  );
};

const StyledTableDataCell = styled(TableDataCell)`
  vertical-align: middle;
`;

const ActionTableDataCell = styled(TableDataCell)`
  ${({ theme }) => `
    &:last-child {
      padding-right: ${theme.spacings[1]};
    }
  `}
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
    height: calc(${theme.spacings[1]} * 3);
    width: calc(${theme.spacings[1]} * 3);
`,
);
